import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/UseTranslation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import "./Navbar.css";
import { getMenuItems } from "../constants/MenuItems";
import MenuItem from "../../models/struct/MenuItem";
import { apiCall } from "../../api/apiCall";
import { User } from "../../models/user";
import TokenStore from "../../api/tokenStore";
import UserIdStore from "../../api/userIdStore";
import ConfirmLogoutDialog from "./ConfirmLogoutDialog";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    return savedState === "true";
  });
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const profileMenuRef = useRef<HTMLDivElement>(null);

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  const { data: currentUser } = useQuery<User, Error>({
    queryKey: ["fetchCurrentUser"],
    queryFn: async () =>
      apiCall<User>("/users/me", {
        method: "GET",
      }),
    enabled: isAuthReady && isAuthenticated,
  });

  const userToGlimpse = currentUser as any;

  const userSubscription =
    userToGlimpse?.Company?.Subscription ||
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;

  const currentPlan =
    userSubscription?.status === "active" ? userSubscription.plan_code : "free";

  useEffect(() => {
    const role = currentUser?.role?.name;
    setItems(getMenuItems(role, t));
  }, [currentUser, t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;

      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(clickedNode)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    document.body.classList.toggle("sidebar-is-collapsed", isCollapsed);

    return () => {
      document.body.classList.remove("sidebar-is-collapsed");
    };
  }, [isCollapsed]);

  const toggleMenu = () => {
    setIsOpen((previousValue) => !previousValue);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const toggleCollapse = () => {
    setIsCollapsed((previousValue) => {
      const newState = !previousValue;
      localStorage.setItem("sidebarCollapsed", String(newState));
      return newState;
    });
  };

  const toggleSubmenu = (label: string) => {
    setOpenSubmenus((previousValue) => ({
      ...previousValue,
      [label]: !previousValue[label],
    }));
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    closeMenu();
    setIsProfileMenuOpen(false);
  };

  const openLogoutDialog = () => {
    setIsProfileMenuOpen(false);
    setIsLogoutDialogOpen(true);
  };

  const closeLogoutDialog = () => {
    setIsLogoutDialogOpen(false);
  };

  const confirmLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
    } catch (error) {
      console.error("Erreur lors de la déconnexion Firebase :", error);
    }

    TokenStore.clearToken();

    if (UserIdStore?.clearUserId) {
      UserIdStore.clearUserId();
    }

    queryClient.clear();

    setIsProfileMenuOpen(false);
    setIsLogoutDialogOpen(false);
    navigate("/");
  };

  const user = currentUser as any;

  const firstName =
    user?.first_name ?? user?.firstName ?? user?.firstname ?? "";

  const lastName = user?.last_name ?? user?.lastName ?? user?.lastname ?? "";

  const userEmail = user?.email ?? "";

  const userInitials =
    firstName || lastName
      ? `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase()
      : (userEmail[0] ?? "U").toUpperCase();

  const userFullName =
    [firstName, lastName].filter(Boolean).join(" ") ||
    userEmail ||
    t.nav.defaultUser;

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          className="burger"
          onClick={toggleMenu}
          aria-label={t.nav.openMenu}
        >
          <div />
          <div />
          <div />
        </button>
      )}

      {isOpen && (
        <div className="overlay" onClick={closeMenu} aria-hidden="true" />
      )}

      <aside
        className={[
          "sidebar-container",
          isOpen ? "sidebar-open" : "",
          isCollapsed ? "sidebar-collapsed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="sidebar-header">
          <div
            onClick={() => handleNavigate("/dashboard")}
            className="sidebar-logo-wrapper"
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                handleNavigate("/dashboard");
              }
            }}
          >
            <img
              src="/logo_notacito_clean.svg"
              alt={t.nav.notacitoLogo}
              className="sidebar-logo-img"
            />

            <h1 className="logo logo-text">Notacito</h1>
          </div>

          <button
            type="button"
            className="collapse-btn"
            onClick={toggleCollapse}
            title={isCollapsed ? t.nav.expandMenu : t.nav.collapseMenu}
            aria-label={isCollapsed ? t.nav.expandMenu : t.nav.collapseMenu}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />

              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {items.map((item) => {
              const itemKey =
                item.path ?? `${item.label}-${item.children?.length ?? 0}`;

              const isSubmenuOpen = openSubmenus[item.label] ?? false;

              if (item.children) {
                return (
                  <li
                    key={itemKey}
                    style={{
                      listStyle: "none",
                      position: "relative",
                    }}
                  >
                    <button
                      type="button"
                      className="sidebar-link-button"
                      onClick={() => toggleSubmenu(item.label)}
                      aria-expanded={isSubmenuOpen}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {item.icon}

                        <span
                          className="menu-label"
                          style={{ fontWeight: 600 }}
                        >
                          {item.label}
                        </span>
                      </div>

                      <span
                        className="submenu-indicator"
                        style={{
                          fontSize: "10px",
                          transition: "transform 0.2s",
                          transform: isSubmenuOpen
                            ? "rotate(120deg)"
                            : "rotate(-90deg)",
                        }}
                      >
                        ▼
                      </span>
                    </button>

                    {(isSubmenuOpen || isCollapsed) && (
                      <ul className="nav-sublist">
                        {isCollapsed && (
                          <li className="nav-sublist-header">{item.label}</li>
                        )}

                        {item.children.map((child) => (
                          <li
                            key={child.path ?? child.label}
                            style={{ listStyle: "none" }}
                          >
                            <button
                              type="button"
                              className={[
                                "sidebar-link-button",
                                "child-link",
                                location.pathname === child.path
                                  ? "active-sidebar-link"
                                  : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => {
                                if (child.path) {
                                  handleNavigate(child.path);
                                }
                              }}
                            >
                              <span className="menu-label">{child.label}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li
                  key={itemKey}
                  style={{
                    listStyle: "none",
                    position: "relative",
                  }}
                >
                  <button
                    type="button"
                    className={[
                      "sidebar-link-button",
                      location.pathname === item.path
                        ? "active-sidebar-link"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (item.path) {
                        handleNavigate(item.path);
                      }
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      {item.icon}

                      <span className="menu-label">{item.label}</span>
                    </div>
                  </button>

                  {isCollapsed && (
                    <div className="collapsed-tooltip">{item.label}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-bottom">
          {currentPlan !== "enterprise" && (
            <div className="notacito-plus-card">
              <h4>⭐ Notacito Plus</h4>

              <p>{t.nav.plusDescription}</p>

              <button type="button" onClick={() => handleNavigate("/upgrade")}>
                {t.nav.viewOffer}
              </button>
            </div>
          )}

          <div className="sidebar-profile-wrapper" ref={profileMenuRef}>
            {isProfileMenuOpen && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-header">
                  <span className="profile-dropdown-label">
                    {t.nav.profileOverview}
                  </span>
                </div>

                <div
                  className="profile-dropdown-user"
                  onClick={() => handleNavigate("/account")}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleNavigate("/account");
                    }
                  }}
                >
                  <div className="profile-dropdown-avatar">{userInitials}</div>

                  <span className="profile-dropdown-name">{userFullName}</span>

                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>

                <div className="profile-dropdown-divider" />

                <button
                  type="button"
                  className="profile-dropdown-item"
                  onClick={() => handleNavigate("/account")}
                >
                  {t.nav.account}
                </button>

                <button
                  type="button"
                  className="profile-dropdown-item"
                  onClick={() => handleNavigate("/upgrade")}
                >
                  {t.nav.manageSubscription}
                </button>

                <div className="profile-dropdown-divider" />

                <button
                  type="button"
                  className="profile-dropdown-item profile-dropdown-item--danger"
                  onClick={openLogoutDialog}
                >
                  {t.nav.logout}
                </button>
              </div>
            )}

            <div
              className="sidebar-user-profile"
              onClick={() =>
                setIsProfileMenuOpen((previousValue) => !previousValue)
              }
              role="button"
              tabIndex={0}
              aria-expanded={isProfileMenuOpen}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setIsProfileMenuOpen((previousValue) => !previousValue);
                }
              }}
            >
              <div className="sidebar-user-avatar">{userInitials}</div>

              {!isCollapsed && (
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{userFullName}</span>

                  {userEmail && (
                    <span className="sidebar-user-email">{userEmail}</span>
                  )}
                </div>
              )}

              {!isCollapsed && (
                <button
                  type="button"
                  className="sidebar-user-menu-btn"
                  title={t.nav.options}
                  aria-label={t.nav.options}
                  onClick={(event) => {
                    event.stopPropagation();

                    setIsProfileMenuOpen((previousValue) => !previousValue);
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle
                      cx="12"
                      cy="5"
                      r="1.5"
                      fill="currentColor"
                      stroke="none"
                    />

                    <circle
                      cx="12"
                      cy="12"
                      r="1.5"
                      fill="currentColor"
                      stroke="none"
                    />

                    <circle
                      cx="12"
                      cy="19"
                      r="1.5"
                      fill="currentColor"
                      stroke="none"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>
      <ConfirmLogoutDialog
        isOpen={isLogoutDialogOpen}
        title={t.common.logoutTitle}
        content={t.common.logoutContent}
        cancelText={t.common.cancel}
        confirmText={t.common.logoutConfirm}
        onCancel={closeLogoutDialog}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default Navbar;
